import {
    Box,
    Button,
    ChakraProps,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Textarea,
} from '@chakra-ui/react';
import BulletPoints from 'components/atoms/BulletPoints';
import { useTranslation } from 'helpers/languages/translations';
import { useEffect, useState } from 'react';
import { linkStyle } from 'shared/styles';
import { nutriscoreColors, TProductDetailsHooks } from '../../useProductDetails';
import useModal from './useModal';

interface IProps {
    dataToSubmit: TProductDetailsHooks['dataToSubmit'];
    setDataToSubmit: TProductDetailsHooks['setDataToSubmit'];
    setIsModalOpen: TProductDetailsHooks['setIsModalOpen'];
    isModalOpen: TProductDetailsHooks['isModalOpen'];
    modalType: TProductDetailsHooks['modalType'];
    isDeleteLoading: TProductDetailsHooks['isDeleteLoading'];
    deletePDPs: TProductDetailsHooks['deletePDPs'];
    lang: TProductDetailsHooks['lang'];
    retailer: TProductDetailsHooks['retailer'];
    dateInWeeks: TProductDetailsHooks['dateInWeeks'];
    readonly?: boolean;
    limitedAccess?: boolean;
    todaysDate: TProductDetailsHooks['todaysDate'];
}

const PDPModal = ({
    dataToSubmit,
    setDataToSubmit,
    deletePDPs,
    isDeleteLoading,
    setIsModalOpen,
    isModalOpen,
    modalType,
    lang,
    retailer,
    todaysDate,
    dateInWeeks,
    readonly,
    limitedAccess
}: IProps) => {
    const {
        isAddPDPLoading,
        isPDPUpdateLoading,
        handleDeletePDP,
        handleCloseModal,
        handleSubmit,
        handleInputChange,
        updateInitialRef,
    } = useModal({
        dataToSubmit,
        setDataToSubmit,
        setIsModalOpen,
        modalType,
        deletePDPs,
        isDeleteLoading,
        lang,
        retailer,
        dateInWeeks
    });

    const [hoveredNutriscore, setHoveredNutriscore] = useState<string | null>(null);
    const [disableFields, setDisableFields] = useState<boolean>(false);


    useEffect(() => {
        if (readonly || (limitedAccess && (todaysDate.current.week !== dateInWeeks.week || todaysDate.current.year !== dateInWeeks.year)))
            setDisableFields(true);
        else setDisableFields(false);
    }, [readonly, limitedAccess, dateInWeeks]);

    const translate = useTranslation();

    const inputStyles: Omit<ChakraProps, never> = {
        mb: '8px',
        fontSize: 'sm',
        width: "200px"
    };

    const setBulletPoints = (values: string[]) => {
        setDataToSubmit((prev) => ({
            ...prev,
            bulletPoints: values,
        }));

    }

    const modalTitle =
        modalType === 'add'
            ? translate('pdp.modal.title.add')
            : translate('pdp.modal.title.update');

    return (
        <Modal
            initialFocusRef={updateInitialRef}
            size="md"
            isOpen={isModalOpen}
            onClose={() => {
                handleCloseModal();
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef} display="flex" alignItems="center">
                    {modalTitle}
                </ModalHeader>
                <ModalCloseButton
                    mt="2"
                    isDisabled={modalType === 'add' ? isAddPDPLoading : isPDPUpdateLoading}
                />

                <ModalBody>

                    <Flex flex="1" mb="20px" alignItems="center" >
                        <Text {...inputStyles}>{translate('pdp.modal.field.ean')}</Text>
                        <Input
                            value={dataToSubmit.ean}
                            onChange={(e) => handleInputChange(e.target.value, 'ean')}
                            isDisabled={disableFields}
                        />
                    </Flex>

                    <Flex flex="1" mb="20px" alignItems="center" >
                        <Text {...inputStyles}>{translate('pdp.modal.field.label')}</Text>
                        <Input
                            value={dataToSubmit.label}
                            onChange={(e) => handleInputChange(e.target.value, 'label')}
                            isDisabled={disableFields}
                        />
                    </Flex>

                    <Flex flex="1" mb="20px" alignItems="flex-start" >
                        <Text {...inputStyles}>{translate('pdp.modal.field.description')}</Text>
                        <Textarea
                            rows={4}
                            resize="none"
                            value={dataToSubmit.description}
                            onChange={(e) => handleInputChange(e.target.value, 'description')}
                            isDisabled={disableFields}
                        />
                    </Flex>


                    <Flex flex="1" mb="20px" alignItems="center" >
                        <Text {...inputStyles} width="191px">{translate('pdp.modal.field.bullet_point')}</Text>
                        <BulletPoints inputs={dataToSubmit.bulletPoints && dataToSubmit.bulletPoints.length ? dataToSubmit.bulletPoints : [""]} setInputs={setBulletPoints} border="1px solid #ccc" borderRadius="7px" padding="10px 0px"
                            isDisabled={disableFields}
                        />
                    </Flex>


                    <Flex flex="1" mb="20px" alignItems="center" height={'50px'} justifyContent="flex-start">
                        <Text {...inputStyles} width="130px">{translate('pdp.modal.field.nutriscore')}</Text>
                        <Flex alignItems="center" >
                            {Object.entries(nutriscoreColors).map(([score, color]) => (
                                <Box
                                    key={score}
                                    width={dataToSubmit.nutriscore === score ? '36px' : '32px'}
                                    height={dataToSubmit.nutriscore === score ? '48px' : '32px'}
                                    bg={
                                        dataToSubmit.nutriscore === score || hoveredNutriscore === score
                                            ? color
                                            : `${color}80`
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color="white"
                                    fontWeight="bold"
                                    borderRadius={dataToSubmit.nutriscore === score ? '8px' : '0'}
                                    border={dataToSubmit.nutriscore === score ? '2px solid white' : 'none'}
                                    cursor={disableFields ? 'default' : 'pointer'}
                                    onClick={
                                        () => {
                                            if (disableFields) return;
                                            handleInputChange(score, 'nutriscore')
                                        }

                                    }
                                    onMouseEnter={e => {
                                        if (disableFields) return;
                                        setHoveredNutriscore(score);
                                    }}
                                    onMouseLeave={e => {
                                        if (disableFields) return;
                                        setHoveredNutriscore(null);
                                    }}
                                >
                                    {score}
                                </Box>
                            ))}
                        </Flex>
                        {
                            !disableFields && (
                                <Box
                                    ml="10px"
                                    cursor={dataToSubmit.nutriscore ? 'pointer' : 'default'}
                                    fontSize="20px"
                                    lineHeight="20px"
                                    fontWeight="bold"
                                    color={dataToSubmit.nutriscore ? 'gray.500' : 'gray.200'}
                                    onClick={() => handleInputChange('', 'nutriscore')}
                                >
                                    &times;
                                </Box>
                            )
                        }
                    </Flex>



                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {modalType === 'update' && !disableFields &&
                        (
                            <Box
                                {...linkStyle}
                                color="red.500"
                                onClick={handleDeletePDP}
                            >
                                {translate('modal.button.delete')}
                            </Box>
                        )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={
                                modalType === 'add' ? isAddPDPLoading : isPDPUpdateLoading
                            }
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleCloseModal}
                        >
                            {translate('pdp.modal.button.cancel')}
                        </Button>
                        {

                            !disableFields &&
                            <Button
                                onClick={handleSubmit}
                                isLoading={modalType === 'add' ? isAddPDPLoading : isPDPUpdateLoading}
                                size="sm"
                                colorScheme="blue"
                                bg="blueMain"
                                isDisabled={!dataToSubmit?.ean?.trim()}
                            >
                                {translate('pdp.modal.button.confirm')}
                            </Button>

                        }
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PDPModal;
